import {
  select as d3Select,
  arc as d3Arc,
  pie as d3Pie
} from 'd3'

import {
  fontWeightSemiBold,
  fontWeightRegular,
  fontSizeXs,
  neutralColorLowPure,
  borderRadiusSm,
  neutralColorLowLight
} from '@deskfy/unify-design-tokens/jsvariables'

/* @ngInject */
function AnalyticsDonutChartController ($rootScope, $scope, $attrs, $translate, Utils) {
  const MAX_PERCENTAGE = 100
  const height = 150
  const width = 150

  $scope.flags = {
    legend: {
      maxPerColumn: 4,
      isMoreThan4: false
    }
  }

  $scope.chart = {}

  $scope.response = []
  $scope.donutTruncateName = ''

  const api = {
    _initDonutChart,
    _insertDonutChart,
    _removeDonutChart,
    _onMouseOver,
    _onMouseLeave,
    _onAnimateDonut,
    _onResetDonut,
    _getUserLocale,
    _successGetUserLocale,
    _formatPercentage,
    _selectElement,
    _handleEventFromHover,
    _getHalfValue,
    _errorGetUserLocale
  }

  this.getApi = () => api

  activate()

  function activate () {
    api._getUserLocale()
  }

  $scope.showLegend = (legends) => {
    return legends.some(legend => legend.total > 0) && $scope.type === 'GROUPED'
  }

  $scope.showTooltipChart = (slug) => {
    api._handleEventFromHover(slug, (item, arcPathFromSlug, tooltip) => {
      api._onMouseOver(null, item, arcPathFromSlug, $scope.chart, $scope.chart.arc, tooltip)
    })
  }

  $scope.hideTooltipChart = (slug) => {
    api._handleEventFromHover(slug, (_, arcPathFromSlug, tooltip) => {
      api._onMouseLeave(null, null, arcPathFromSlug, tooltip)
    })
  }

  $scope.getIconType = () => {
    const icons = {
      POST: 'posts',
      AUDIT: 'audit',
      ART: 'arts'
    }

    const icon = $scope.data?.[0]?.slug

    return icons[icon] || ''
  }

  $scope.getPeriodText = () => {
    if (!$scope.periodStart || !$scope.periodEnd) {
      return ''
    }

    const date = {
      start: new Date($scope.periodStart),
      end: new Date($scope.periodEnd)
    }

    const dayStart = Utils.formatDate(date.start, $scope.locale, {
      day: 'numeric'
    })

    const dayEnd = Utils.formatDate(date.end, $scope.locale, {
      day: 'numeric'
    })

    const monthStart = Utils.formatDate(date.start, $scope.locale, {
      month: 'short'
    }).replace('.', '')

    const monthEnd = Utils.formatDate(date.end, $scope.locale, {
      month: 'short'
    }).replace('.', '')

    return `${dayStart} ${monthStart} - ${dayEnd} ${monthEnd}`
  }

  function _initDonutChart () {
    const data = $scope.data

    if (!data.length) {
      return
    }

    $scope.donutTruncateName = Utils.truncateText($scope.name)

    const items = data.map(item => ({
      ...item,
      total: parseInt(item.total, 10) || 0,
      totalDone: parseInt(item.totalDone, 10) || 0,
      formattedPercentage: item.percentage !== MAX_PERCENTAGE ? api._formatPercentage(item.percentage) : `${item.percentage}%`
    }))

    const shouldAddRemaining = items.length === 1 && $scope.type === 'GROUPED'

    if (shouldAddRemaining) {
      const data = items?.[0]
      const totalDone = parseInt(data.totalDone, 10) || 0
      const total = parseInt(data.total, 10) || 0
      const percentageDone = parseInt(data.percentage, 10) || 0

      const remainingPercentage = MAX_PERCENTAGE - percentageDone
      const remainingTotal = total - totalDone

      items.push({
        name: '',
        slug: 'REMAINING',
        color: neutralColorLowLight,
        total: remainingTotal > 0 ? remainingTotal : 0,
        totalDone: 0,
        percentage: remainingPercentage,
        formattedPercentage: api._formatPercentage(remainingPercentage),
        isRemaining: true
      })
    }

    const itemsWithPercentage = items.filter(item => item.percentage)
    $scope.chart.hasMoreThanOneValue = itemsWithPercentage.length > 1
    $scope.response = items

    $scope.flags.isMoreThan4 = items.length >= 3

    api._insertDonutChart(itemsWithPercentage)
  }

  function _insertDonutChart (itemsWithPercentage) {
    api._removeDonutChart($scope.chart.element)

    const svg = $scope.chart.element
      .append('svg')
      .attr('width', $scope.chart.width + $scope.chart.marginSize)
      .attr('height', $scope.chart.height + $scope.chart.marginSize)

    const groupSvg = svg
      .append('g')
      .attr('width', $scope.chart.width)
      .attr('height', $scope.chart.height)
      .attr('transform', `translate(${api._getHalfValue($scope.chart.width)}, ${api._getHalfValue($scope.chart.height)})`)

    const padAngle = $scope.type === 'GROUPED' ? 0.05 : 0
    const pie = d3Pie()
      .value(data => data.percentage)
      .sort(null)
      .padAngle(padAngle)

    const tooltip = $scope.chart.element
      .append('div')
      .attr('class', 'tooltip')
      .style('background-color', neutralColorLowPure)
      .style('border-radius', borderRadiusSm)

    groupSvg.selectAll('path')
      .data(pie(itemsWithPercentage))
      .enter()
      .append('path')
      .attr('d', $scope.chart.arc)
      .attr('fill', ({ data }) => data.color)
      .attr('class', ({ data }) => data.slug)
      .on('mouseover', (event, data) => {
        if (!data?.data?.isRemaining) {
          api._onMouseOver(event, data, null, $scope.chart, $scope.chart.arc, tooltip)
        }
      })
      .on('mouseleave', (event, data) => {
        if (!data?.data?.isRemaining) {
          api._onMouseLeave(event, data, null, tooltip)
        }
      })
    groupSvg.append('text')
      .attr('class', 'total-label-text')
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', 'middle')
      .attr('y', -$scope.chart.text.y)
      .style('font-size', fontSizeXs)
      .style('font-weight', fontWeightRegular)
      .text($scope.chart.text.totalLabel)

    groupSvg.append('text')
      .attr('class', 'total-value-text')
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', 'middle')
      .attr('y', $scope.chart.text.y)
      .style('font-size', fontSizeXs)
      .style('font-weight', fontWeightSemiBold)
      .text($scope.chart.getMaxValue($scope.response))
  }

  /**
   * This method remove the donut chart and the tooltip from the DOM
   *
   * used when update the donut chart
   *
   * @param {d3Element} elementWrapper
   */
  function _removeDonutChart (elementWrapper) {
    elementWrapper.select('svg').remove()
    elementWrapper.select('.tooltip').remove()
  }

  /**
   * This method execute when mouse over on donut chart
   *
   * @param {d3Event} event
   * @param {d3Data} event
   * @param {d3Element} elementFromSelect
   * @param {object} chart
   * @param {d3Arc} arc
   * @param {d3Element} tooltipElement
   */
  function _onMouseOver (event, data, elementFromSelect, chart, arc, tooltipElement) {
    if (data?.data?.isRemaining) {
      return
    }

    const arcCentroid = arc.centroid(data)
    const x = arcCentroid[0] + api._getHalfValue(chart.width)
    const y = chart.hasMoreThanOneValue ? arcCentroid[1] + api._getHalfValue(chart.height) : arcCentroid[1] - chart.tooltip.top

    tooltipElement
      .html(`${chart.tooltip.label}: <b>${data.data.total.toLocaleString($scope.locale)}</b>`)
      .style('opacity', 1)
      .style('transform', `translate(calc(-50% + ${x}px), calc(-100% + ${y}px))`)

    if (elementFromSelect) {
      api._onAnimateDonut(elementFromSelect, chart.hasMoreThanOneValue, chart)
    } else {
      api._onAnimateDonut(d3Select(event.target), chart.hasMoreThanOneValue, chart)
    }
  }

  /**
   * This method execute when mouse leave the donut
   *
   * @param {d3Event} event
   * @param {d3Element} elementFromSelect
   * @param {d3Element} tooltipElement
   */
  function _onMouseLeave (event, _, elementFromSelect, tooltipElement) {
    tooltipElement.style('opacity', 0)

    if (elementFromSelect) {
      api._onResetDonut(elementFromSelect, $scope.chart.hasMoreThanOneValue, $scope.chart)
    } else {
      api._onResetDonut(d3Select(event.target), $scope.chart.hasMoreThanOneValue, $scope.chart)
    }
  }

  /**
   * This method starts animation of donut chart
   *
   * @param {d3Element} path
   * @param {boolean} hasMoreThanOneValue
   * @param {object} chart
   */
  function _onAnimateDonut (path, hasMoreThanOneValue, chart) {
    const data = path.datum()
    if (data?.data?.isRemaining) {
      return
    }

    if (hasMoreThanOneValue) {
      path
        .classed('drop-shadow', true)
        .transition()
        .duration(chart.animation.duration)
        .attr('d', d3Arc()
          .innerRadius(chart.radius - (chart.donutArcWidth + chart.animation.innerRadius))
          .outerRadius(chart.radius))
    }
  }

  /**
   * This method reset donut chart, closing the tooltip and remove animation
   *
   * @param {d3Element} path
   * @param {boolean} hasMoreThanOneValue
   * @param {object} chart
   */
  function _onResetDonut (path, hasMoreThanOneValue, chart) {
    if (hasMoreThanOneValue) {
      path
        .classed('drop-shadow', false)
        .transition()
        .duration(chart.animation.duration)
        .attr('d', $scope.chart.arc)
    }
  }

  function _getUserLocale () {
    $rootScope.getUser().then(api._successGetUserLocale).catch(api._errorGetUserLocale)
  }

  function _successGetUserLocale ({ locale }) {
    $scope.locale = locale

    $scope.chart = {
      ...$scope.chart,
      height,
      width,
      element: d3Select($attrs.$$element.find('.donut-chart-svg')[0]),
      getMaxValue: response => {
        const total = response
          .filter(item => !item.isRemaining && item.slug !== 'REMAINING')
          .reduce((acc, value) => acc + value.total, 0)
        if (!total) return '0'
        return Number(total).toLocaleString(locale)
      },
      radius: api._getHalfValue(Math.min(width, height)),
      donutArcWidth: 30,
      marginSize: 5,
      text: {
        y: 15,
        totalLabel: $translate.instant('total')
      },
      tooltip: {
        top: 55,
        label: $translate.instant('dataFrames.donut.tooltips.label')
      },
      animation: {
        innerRadius: 8,
        duration: 200
      }
    }

    $scope.chart.arc = d3Arc()
      .innerRadius($scope.chart.radius - $scope.chart.donutArcWidth)
      .outerRadius($scope.chart.radius)

    api._initDonutChart()
  }

  function _errorGetUserLocale (error) {
    console.log({ error })
  }

  /**
   * This method format the number for string percentage
   *
   * @param {number} value
   * @param {number} maxDigits
   */
  function _formatPercentage (value, maxDigits = 1) {
    const reversedPercentage = (value / 100)
    return reversedPercentage.toLocaleString($scope.locale, { style: 'percent', maximumFractionDigits: maxDigits })
  }

  /**
   * This method get element from element wrapper
   *
   * @param {string | HTMLElement} query
   */
  function _selectElement (query) {
    return $scope.chart.element.select(query)
  }

  /**
   * This method execute an event for hover animation
   *
   * @param {() => void} callback
   */
  function _handleEventFromHover (slug, callback) {
    const tooltip = api._selectElement('.tooltip')
    const arcPathFromSlug = api._selectElement(`.${slug}`)

    $scope.chart.element.selectAll('path').data().forEach((item) => {
      if (item.data.slug === slug && item.value) {
        callback(item, arcPathFromSlug, tooltip)
      }
    })
  }

  /**
   * This method return half of the value
   *
   * @param {number} callback
   */
  function _getHalfValue (value) {
    return value / 2
  }
}

export { AnalyticsDonutChartController }
